import { useState, useMemo } from 'react';
import { Button, Badge, Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiArrowPath, HiLink, HiXMark } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';
import { useReassignDocumentSprawaMutation } from '@/store/documentsApi';
import { useListSprawyQuery } from '@/store/sprawyApi';
import type { DocumentDto, ExtractedDocumentMetadataDto } from '@/types';
import { MetadataApprovalStatus } from '@/types';
import {
  Panel,
  PanelHeader,
  MetadataGrid,
  MetadataItem,
  MetadataLabel,
  MetadataValue,
} from './MetadataApprovalPanel.styled';

interface MetadataApprovalPanelProps {
  document: DocumentDto;
}

export default function MetadataApprovalPanel({ document: doc }: MetadataApprovalPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reassignSprawa] = useReassignDocumentSprawaMutation();
  const [showReassignModal, setShowReassignModal] = useState(false);

  const parsed = useMemo<ExtractedDocumentMetadataDto | null>(() => {
    if (!doc.extractedMetadata) return null;
    try { return JSON.parse(doc.extractedMetadata); } catch { return null; }
  }, [doc.extractedMetadata]);

  // Only show for approved metadata (auto-bound or manual)
  if (doc.metadataApprovalStatus !== MetadataApprovalStatus.Approved || !parsed) {
    return null;
  }

  const isLinked = !!doc.sprawaId;

  const handleUnlink = async () => {
    await reassignSprawa({ id: doc.id, sprawaId: null });
  };

  return (
    <>
      <Panel $linked={isLinked}>
        <PanelHeader>
          <div className="d-flex align-items-center gap-2">
            <h6 className="mb-0">{t('metadata.title')}</h6>
            {isLinked ? (
              <Badge bg="success">{t('metadata.autoLinked')}</Badge>
            ) : (
              <Badge bg="secondary">{t('metadata.noCase')}</Badge>
            )}
          </div>
          <div className="d-flex gap-2">
            {isLinked && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate(`/sprawy/${doc.sprawaId}`)}
              >
                {t('metadata.viewCase')}
              </Button>
            )}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowReassignModal(true)}
            >
              <HiArrowPath size="0.75rem" className="me-1" />
              {t('metadata.reassign')}
            </Button>
            {isLinked && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleUnlink}
              >
                <HiXMark size="0.75rem" className="me-1" />
                {t('metadata.unlink')}
              </Button>
            )}
          </div>
        </PanelHeader>

        <MetadataGrid>
          {parsed.signature && (
            <MetadataItem>
              <MetadataLabel>{t('metadata.signature')}</MetadataLabel>
              <MetadataValue>{parsed.signature}</MetadataValue>
            </MetadataItem>
          )}
          {parsed.court && (
            <MetadataItem>
              <MetadataLabel>{t('metadata.court')}</MetadataLabel>
              <MetadataValue>{parsed.court}</MetadataValue>
            </MetadataItem>
          )}
          {parsed.department && (
            <MetadataItem>
              <MetadataLabel>{t('metadata.department')}</MetadataLabel>
              <MetadataValue>{parsed.department}</MetadataValue>
            </MetadataItem>
          )}
          {parsed.judge && (
            <MetadataItem>
              <MetadataLabel>{t('metadata.judge')}</MetadataLabel>
              <MetadataValue>{parsed.judge}</MetadataValue>
            </MetadataItem>
          )}
          {parsed.subject && (
            <MetadataItem>
              <MetadataLabel>{t('metadata.subject')}</MetadataLabel>
              <MetadataValue>{parsed.subject}</MetadataValue>
            </MetadataItem>
          )}
          {parsed.parties?.length > 0 && (
            <MetadataItem $wide>
              <MetadataLabel>{t('metadata.parties')}</MetadataLabel>
              <MetadataValue>
                {parsed.parties.map((p, i) => (
                  <span key={i}>
                    {p.name}{p.role ? ` (${p.role})` : ''}
                    {i < parsed.parties.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </MetadataValue>
            </MetadataItem>
          )}
        </MetadataGrid>
      </Panel>

      <ReassignModal
        show={showReassignModal}
        documentId={doc.id}
        onHide={() => setShowReassignModal(false)}
      />
    </>
  );
}

function ReassignModal({ show, documentId, onHide }: {
  show: boolean;
  documentId: string;
  onHide: () => void;
}) {
  const { t } = useTranslation();
  const [reassignSprawa, { isLoading }] = useReassignDocumentSprawaMutation();
  const [selectedSprawaId, setSelectedSprawaId] = useState('');
  const { data: sprawyData } = useListSprawyQuery({ page: 1, pageSize: 50 }, { skip: !show });

  const handleReassign = async () => {
    if (!selectedSprawaId) return;
    await reassignSprawa({ id: documentId, sprawaId: selectedSprawaId });
    onHide();
    setSelectedSprawaId('');
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('metadata.reassignTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>{t('metadata.selectCase')}</Form.Label>
          <Form.Select
            value={selectedSprawaId}
            onChange={(e) => setSelectedSprawaId(e.target.value)}
          >
            <option value="">{t('metadata.selectCasePlaceholder')}</option>
            {sprawyData?.items?.map((sprawa) => (
              <option key={sprawa.id} value={sprawa.id}>
                {sprawa.signature}
                {sprawa.court ? ` — ${sprawa.court}` : ''}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={!selectedSprawaId || isLoading}
          onClick={handleReassign}
        >
          <HiLink size="0.75rem" className="me-1" />
          {t('metadata.reassignConfirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
